import { VideoThumbnail } from "@/components/video";
import { useBoolean } from "@/hooks/useBoolean";
import useHoldClick from "@/hooks/useHoldClick";
import { cn } from "@/libraries/cn";
import { supportedImageExtensions } from "@/libraries/constant";
import type { Storage } from "@/types";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { ListItemText } from "@mui/material";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface ItemImageProps extends React.ComponentProps<"div"> {
	file: Storage;
	allowContextMenu?: boolean;
	onDelete: () => void;
	onEdit: () => void;
	onHold: () => void;
}

const ItemImage = forwardRef<HTMLImageElement, ItemImageProps>(
	(
		{
			file,
			onDelete,
			onEdit,
			onHold,
			className,
			allowContextMenu = false,
			...props
		},
		ref,
	) => {
		const { t } = useTranslation("common");
		const { elRef: wrapperRef } = useHoldClick({ onHold });
		const {
			value: isOpenContextMenu,
			setTrue: openContextMenu,
			setFalse: closeContextMenu,
		} = useBoolean();

		const fileURL = useMemo(() => new URL(file.file_id), [file]);

		const onContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
			if (!allowContextMenu) return;
			e.stopPropagation();
			e.preventDefault();
			return openContextMenu();
		};

		const onCloseContextMenu = (e: MouseEvent) => {
			e.stopPropagation();
			e.preventDefault();
			return closeContextMenu();
		};

		const onClickDelete = (e: React.MouseEvent<HTMLLIElement>) => {
			e.stopPropagation();
			e.preventDefault();
			return onDelete();
		};

		const onClickEdit = (e: React.MouseEvent<HTMLLIElement>) => {
			e.stopPropagation();
			e.preventDefault();
			return onEdit();
		};

		return (
			<div
				ref={wrapperRef}
				className={cn("relative", className)}
				onContextMenu={onContextMenu}
				{...props}
			>
				{supportedImageExtensions.some((ext) =>
					fileURL.pathname.endsWith(ext),
				) ? (
					<img
						ref={ref}
						src={file?.file_id}
						alt={file?.id?.toString()}
						className="object-cover h-48 w-full rounded-md"
						loading="eager"
					/>
				) : (
					<VideoThumbnail src={file?.file_id} className="h-full" />
				)}
				{allowContextMenu ? (
					<Menu
						anchorEl={wrapperRef.current}
						open={isOpenContextMenu}
						onClose={onCloseContextMenu}
						anchorOrigin={{
							vertical: "center",
							horizontal: "center",
						}}
						transformOrigin={{
							vertical: "center",
							horizontal: "center",
						}}
						MenuListProps={{
							"aria-labelledby": "gallery-item-context-menu",
						}}
					>
						<MenuItem onClick={onClickEdit}>
							<ListItemIcon>
								<EditIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText>{t("common:edit")}</ListItemText>
						</MenuItem>
						<MenuItem onClick={onClickDelete}>
							<ListItemIcon>
								<DeleteIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText>{t("common:delete")}</ListItemText>
						</MenuItem>
					</Menu>
				) : null}
			</div>
		);
	},
);

export default ItemImage;
